/**
 * Email batch document schemas for client SDK.
 *
 * Contains SDK-specific schemas that transform timestamps to Timestamp instances,
 * plus typed document refs.
 */
import type {
  DocumentReference,
  CollectionReference,
  Firestore,
} from 'firebase/firestore';
import {z} from 'zod';
import {doc, collection, getFirestore} from 'firebase/firestore';

import {
  EmailBatchDocumentSchema,
  getEmailBatchCollectionPath,
  getEmailBatchDocumentPath,
} from '../documents/emailBatch';
import {ClientTimestampSchema, createConverter} from './sdk';

// ============================================================================
// SDK-Specific Schema (with Timestamp transform)
// ============================================================================

export const EmailBatchSchema = EmailBatchDocumentSchema.extend({
  createdAt: ClientTimestampSchema,
}).loose();

export type EmailBatch = z.infer<typeof EmailBatchSchema>;

// ============================================================================
// Document Refs
// ============================================================================

const emailBatchConverter = createConverter({
  schema: EmailBatchSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

export function getEmailBatchDocRef({
  courseId,
  batchId,
  firestore = getFirestore(),
}: {
  courseId: string;
  batchId: string;
  firestore?: Firestore;
}): DocumentReference<EmailBatch> {
  return doc(
    firestore,
    getEmailBatchDocumentPath({courseId, batchId}),
  ).withConverter(emailBatchConverter);
}

export function getEmailBatchCollectionRef({
  courseId,
  firestore = getFirestore(),
}: {
  courseId: string;
  firestore?: Firestore;
}): CollectionReference<EmailBatch> {
  return collection(
    firestore,
    getEmailBatchCollectionPath({courseId}),
  ).withConverter(emailBatchConverter);
}
